import React, { useEffect, useState } from "react";
import {
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiHorizontalRule,
  EuiPanel,
  EuiSelect,
  EuiSpacer,
  EuiSwitch,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useApi } from "../";
import { useAnketaAuth } from "../auth/context";
import { useAnketaI18n } from "src/internationalization";
import { TableSkeleton } from "src/components/skeleton";
import { AdminSettings } from "./settings";

type AiAlertChannel = "webhook" | "email";

interface AiAlertDestination {
  vpc: string;
  name: string | null;
  channel: AiAlertChannel;
  target: string;
  minEvent: string;
  enabled: boolean;
}

interface AiAlertDestinationsResponse {
  destinations: AiAlertDestination[];
  max: number;
  events: string[];
}

/** Fallback ordering, only used if the backend response is unreadable. */
const FALLBACK_EVENTS = [
  "threshold_crossed",
  "projected_limit_exceeded",
  "budget_crossed",
];

const EVENT_LABELS: Record<string, string> = {
  threshold_crossed: "Schwelle erreicht (Warnung)",
  projected_limit_exceeded: "Prognose: Limit wird gerissen",
  budget_crossed: "Limit erreicht (nur harte Grenze)",
};

const emptyDestination = (): AiAlertDestination => ({
  vpc: "",
  name: "",
  channel: "webhook",
  target: "",
  minEvent: FALLBACK_EVENTS[0],
  enabled: true,
});

/**
 * Editor for the per-customer AI budget-alert destinations (superuser-only).
 *
 * The gateway already sends every budget event to the internal room; these rows
 * decide which of them are forwarded onward, and to whom. That makes this a
 * disclosure control, not a convenience: a wildcard row (VPC left empty) sees
 * every customer's spend.
 *
 * Saving PUTs the whole list to /api/v2/admin/ai-alert-destinations; the backend
 * caps, validates and renumbers it.
 */
export const AdminAiAlertDestinations = (): JSX.Element => {
  const { Get, Put } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<boolean>(false);

  const [max, setMax] = useState<number>(10);
  const [events, setEvents] = useState<string[]>(FALLBACK_EVENTS);
  const [destinations, setDestinations] = useState<AiAlertDestination[]>([]);

  const load = async () => {
    setLoading(true);
    setError(null);
    const resp = await Get<any>({
      path: "v2/admin/ai-alert-destinations",
      responseType: "json",
      token: keycloak?.token,
    });
    // useApi never throws on an HTTP error - it returns success:false - so the
    // check has to be explicit; a try/catch here would be dead code.
    const payload: AiAlertDestinationsResponse | null = resp?.success
      ? ((resp?.data as any)?.data ?? null)
      : null;
    if (!payload) {
      setError(
        translate("anketa.admin.aiAlerts.load.error", {
          defaultMessage: "Destinations could not be loaded.",
        }),
      );
      setLoading(false);
      return;
    }
    setMax(payload.max ?? 10);
    setEvents(payload.events?.length ? payload.events : FALLBACK_EVENTS);
    setDestinations(
      (payload.destinations ?? []).map((d) => ({
        vpc: d.vpc ?? "",
        name: d.name ?? "",
        channel: d.channel === "email" ? "email" : "webhook",
        target: d.target,
        minEvent: d.minEvent ?? FALLBACK_EVENTS[0],
        enabled: d.enabled !== false,
      })),
    );
    setLoading(false);
  };

  useEffect(() => {
    if (keycloak?.token) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const update = (index: number, patch: Partial<AiAlertDestination>) => {
    setSaved(false);
    setDestinations((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    );
  };

  const remove = (index: number) => {
    setSaved(false);
    setDestinations((prev) => prev.filter((_, i) => i !== index));
  };

  const add = () => {
    setSaved(false);
    setDestinations((prev) => [
      ...prev,
      { ...emptyDestination(), minEvent: events[0] },
    ]);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    const resp = await Put<any>({
      path: "v2/admin/ai-alert-destinations",
      responseType: "json",
      token: keycloak?.token,
      value: {
        destinations: destinations.map((d) => ({
          vpc: d.vpc?.trim() ?? "",
          name: d.name?.trim() ? d.name.trim() : null,
          channel: d.channel,
          target: d.target.trim(),
          minEvent: d.minEvent,
          enabled: d.enabled,
        })),
      },
    });
    if (resp?.success) {
      setSaved(true);
      await load();
    } else {
      const details: string[] | undefined = (resp?.data as any)?.data?.details;
      setError(
        details?.length
          ? details.join(" · ")
          : translate("anketa.admin.save.error", {
              defaultMessage: "Saving failed.",
            }),
      );
    }
    setSaving(false);
  };

  if (loading) {
    return <TableSkeleton rows={8} />;
  }

  return (
    <>
      {/* Where anketa posts the operators' copy of every budget alert
          (AI_ALERT_WEBHOOK_URL). Its own room rather than the general operator
          one, because cost chatter is what a team eventually wants elsewhere;
          empty falls back to the Settings-tab hook.
          One field is enough for the whole fleet: there is one LiteLLM per VPC,
          but the receiver identifies which VPC an alert came from, so the room
          does not have to be per-cluster. A per-VPC room can still be set in the
          questionnaire's AI section, and anketa honours it. */}
      <EuiTitle size="xs">
        <h4>
          {translate("anketa.admin.aiAlerts.destination.section", {
            defaultMessage: "Where cost alerts go",
          })}
        </h4>
      </EuiTitle>
      <EuiSpacer size="s" />
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.aiAlerts.destination.help", {
          defaultMessage:
            "anketa posts every budget event here. Empty falls back to the operator RocketChat hook on the Settings tab. anketa checks the response, so a hook that refuses us is reported as a failed destination rather than silently dropped.",
        })}
      </EuiText>
      <EuiSpacer size="s" />
      <AdminSettings group="aiAlerts" />

      <EuiSpacer size="l" />
      <EuiHorizontalRule />
      <EuiSpacer size="m" />

      <EuiTitle size="xs">
        <h4>
          {translate("anketa.admin.aiAlerts.perCustomer.section", {
            defaultMessage: "Per-customer forwarding",
          })}
        </h4>
      </EuiTitle>
      <EuiSpacer size="s" />
      <EuiText size="s" color="subdued">
        {translate("anketa.admin.aiAlerts.description", {
          values: { max },
          defaultMessage:
            "The gateway sends every budget event to anketa, which always posts the operators' copy to the RocketChat hook from Settings. The destinations here forward a customer's own events onward as well. Leave the VPC empty to match every customer - a wildcard row therefore sees every customer's spend. Up to {{max}} destinations. E-mail rows are sent by anketa over its own transport (Microsoft Graph where configured).",
        })}
      </EuiText>
      <EuiSpacer size="m" />

      {error && (
        <>
          <EuiCallOut title={error} color="danger" iconType="alert" />
          <EuiSpacer size="m" />
        </>
      )}
      {saved && (
        <>
          <EuiCallOut
            title={translate("anketa.admin.aiAlerts.saved", {
              defaultMessage: "Destinations saved.",
            })}
            color="success"
            iconType="check"
          />
          <EuiSpacer size="m" />
        </>
      )}

      {destinations.length === 0 && (
        <EuiText size="s" color="subdued">
          {translate("anketa.admin.aiAlerts.empty", {
            defaultMessage:
              "No destinations configured - budget alerts stay internal.",
          })}
        </EuiText>
      )}

      {destinations.map((dest, index) => (
        <React.Fragment key={index}>
          <EuiPanel hasShadow={false} hasBorder paddingSize="m">
            <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
              <EuiFlexItem grow={false}>
                <EuiTitle size="xxs">
                  <h4>
                    {translate("anketa.admin.aiAlerts.destination.title", {
                      values: { index: index + 1 },
                      defaultMessage: "Destination {{index}}",
                    })}
                  </h4>
                </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiSwitch
                  label={translate("anketa.admin.aiAlerts.enabled.label", {
                    defaultMessage: "Active",
                  })}
                  checked={dest.enabled}
                  onChange={(e) => update(index, { enabled: e.target.checked })}
                />
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />

            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate("anketa.admin.aiAlerts.name.label", {
                    defaultMessage: "Name (optional)",
                  })}
                >
                  <EuiFieldText
                    placeholder={translate(
                      "anketa.admin.aiAlerts.name.placeholder",
                      { defaultMessage: "e.g. Cost mailbox" },
                    )}
                    value={dest.name ?? ""}
                    onChange={(e) => update(index, { name: e.target.value })}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate("anketa.admin.aiAlerts.vpc.label", {
                    defaultMessage: "VPC (empty = all)",
                  })}
                  helpText={translate("anketa.admin.aiAlerts.vpc.help", {
                    defaultMessage: "Site name, e.g. opsanio-tiger-lily",
                  })}
                >
                  <EuiFieldText
                    placeholder="opsanio-tiger-lily"
                    value={dest.vpc}
                    onChange={(e) => update(index, { vpc: e.target.value })}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiFormRow
                  label={translate("anketa.admin.aiAlerts.channel.label", {
                    defaultMessage: "Channel",
                  })}
                >
                  <EuiSelect
                    options={[
                      { value: "webhook", text: "Webhook" },
                      { value: "email", text: "E-Mail (via Gateway)" },
                    ]}
                    value={dest.channel}
                    onChange={(e) =>
                      update(index, {
                        channel:
                          e.target.value === "email" ? "email" : "webhook",
                      })
                    }
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />

            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiFormRow
                  label={
                    dest.channel === "email"
                      ? translate("anketa.admin.aiAlerts.recipients.label", {
                          defaultMessage: "Recipients (comma-separated)",
                        })
                      : translate("anketa.admin.aiAlerts.url.label", {
                          defaultMessage: "Webhook URL (https)",
                        })
                  }
                >
                  <EuiFieldText
                    placeholder={
                      dest.channel === "email"
                        ? "cost@example.net"
                        : "https://chat.example.net/hooks/..."
                    }
                    value={dest.target}
                    onChange={(e) => update(index, { target: e.target.value })}
                  />
                </EuiFormRow>
              </EuiFlexItem>
              <EuiFlexItem>
                <EuiFormRow
                  label={translate("anketa.admin.aiAlerts.minEvent.label", {
                    defaultMessage: "Forward from",
                  })}
                  helpText={translate("anketa.admin.aiAlerts.minEvent.help", {
                    defaultMessage:
                      "Least urgent event still forwarded. More urgent ones always are.",
                  })}
                >
                  <EuiSelect
                    options={events.map((event) => ({
                      value: event,
                      text: EVENT_LABELS[event] ?? event,
                    }))}
                    value={dest.minEvent}
                    onChange={(e) =>
                      update(index, { minEvent: e.target.value })
                    }
                  />
                </EuiFormRow>
              </EuiFlexItem>
            </EuiFlexGroup>

            <EuiSpacer size="s" />
            <EuiButtonEmpty
              color="danger"
              iconType="trash"
              size="s"
              onClick={() => remove(index)}
            >
              {translate("anketa.admin.aiAlerts.remove.button", {
                defaultMessage: "Remove",
              })}
            </EuiButtonEmpty>
          </EuiPanel>
          <EuiSpacer size="m" />
        </React.Fragment>
      ))}

      {destinations.length < max && (
        <>
          <EuiButtonEmpty iconType="plusInCircle" onClick={add}>
            {translate("anketa.admin.aiAlerts.add.button", {
              defaultMessage: "Add destination",
            })}
          </EuiButtonEmpty>
          <EuiSpacer size="m" />
        </>
      )}

      <EuiHorizontalRule />

      <EuiButton fill onClick={save} isLoading={saving}>
        {translate("anketa.admin.save.button", { defaultMessage: "Save" })}
      </EuiButton>{" "}
      <EuiButton onClick={load} isDisabled={saving}>
        {translate("anketa.admin.reset.button", { defaultMessage: "Reset" })}
      </EuiButton>
    </>
  );
};
